interface FacadeErrorBody {
  // Nest returns a string for thrown exceptions and a string[] for DTO validation failures.
  message?: unknown;
}

interface FacadeErrorLike {
  response?: { data?: FacadeErrorBody };
  message?: unknown;
}

// Facade errors carry `response.data.message`; axios only exposes "Request failed with status code N".
export function extractErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (typeof error !== "object" || error === null) return fallbackMessage;

  const facadeError = error as FacadeErrorLike;
  return (
    toMessageText(facadeError.response?.data?.message) ??
    toMessageText(facadeError.message) ??
    fallbackMessage
  );
}

function toMessageText(message: unknown): string | null {
  if (typeof message === "string") {
    const text = message.trim();
    if (text.length > 0) return text;
  }

  if (Array.isArray(message)) {
    const lines = message
      .filter((line): line is string => typeof line === "string")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (lines.length > 0) return lines.join(", ");
  }

  return null;
}

/** The batch a re-uploaded file already became, when the server answered 409 with one. */
export function duplicateFrom(error: unknown): { batchId: string; orderCount: number; confirmedAt: string } | null {
  if (typeof error !== "object" || error === null) return null;
  const body = (error as { response?: { status?: number; data?: { duplicateOf?: unknown } } }).response;
  if (body?.status !== 409) return null;
  const duplicateOf = body.data?.duplicateOf as { batchId?: unknown; orderCount?: unknown; confirmedAt?: unknown } | undefined;
  if (typeof duplicateOf?.batchId !== "string") return null;
  return {
    batchId: duplicateOf.batchId,
    orderCount: Number(duplicateOf.orderCount ?? 0),
    confirmedAt: String(duplicateOf.confirmedAt ?? ""),
  };
}
